import { FC, useState } from "react";
import {
  formatPrice,
  getEnabledPeriodPrices,
  splitPeriodRows,
  cardPhotoUrl,
  dailyRate,
  markupPercent,
  moreRatesLabel,
} from "@1-now/booking-core";
import logoSrc from "images/bonzah.png";
import { useData } from "data/data-provider";
import { hasNoPrice } from "utils/carPrice";
import { specNumber, specText } from "utils/carSpecs";
import { priceAreaHeight, TIER_MORE_PX, TIER_ROW_PX } from "utils/tierSlots";
import { useTierSlots } from "utils/useTierSlots";
import RequestPriceButton from "shared/RequestPriceButton/RequestPriceButton";
export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  onClick?: any;
}

const FleetCard: FC<FleetCardProps> = ({
  size = "default",
  className = "",
  data,
  simpleImg = false,
  onClick,
}) => {
  const {
    name,
    color,
    seats,
    allowed_insurances,
    has_bonzah_insurance,
    increased_price,
  } = data ?? {};
  const seatsNum = specNumber(seats);
  const colorText = specText(color);

  // No base price: not bookable, the renter asks for one instead.
  const noPrice = hasNoPrice(data);
  const increasedPriceNum = Number(increased_price);
  // The daily rate and the markup of the crossed-out price over it, by the
  // rule every design's card follows (dailyRate / markupPercent in
  // @1-now/booking-core): the trip average, else the list price. A car priced
  // only on `price` used to show no rate here. A car with no base price
  // shows no amount at all, so it carries no markup either.
  const currentPriceNum = dailyRate(data) ?? 0;
  const markup = noPrice ? null : markupPercent(data);
  const hasDiscount = markup != null;
  const discountPercent = markup ?? 0;
  const { ourCompany } = useData();

  const periodPrices = getEnabledPeriodPrices(
    currentPriceNum,
    ourCompany,
    data?.period_prices,
  );
  const primaryPeriod = periodPrices[0];
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const { shown: secondaryPeriods, hiddenCount } = splitPeriodRows(
    periodPrices.slice(1),
    tiersExpanded,
  );
  // The tier area holds the room for the most tiers any card shows, so the main price sits at
  // the same height in every card of a row (1NOW-11110).
  const slots = useTierSlots(data);
  // The Request price button of a car with no price sits in the area under the price line, the
  // place a priced car keeps for its tiers, so "Price on request" and the other cards' main
  // price are at one height (1NOW-11110). The small card has no such area: its button stays
  // under the text.
  const areaShown = size === "default" && (slots.rows > 0 || !!slots.request);
  const requestButton = noPrice ? (
    <RequestPriceButton
      car={data}
      className="nc-Button inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors bg-primary-6000 hover:bg-primary-700 text-on-primary"
    />
  ) : null;

  const shouldShowBonzahBadge =
    ourCompany?.show_bonzah_tag === true && has_bonzah_insurance;

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full">
        {(function renderBonzahBadge() {
          if (!shouldShowBonzahBadge) return null;
          return (
            <div className="absolute top-2 left-2 z-10 flex items-center space-x-2 px-2 py-1 rounded-md bg-white bg-opacity-90 shadow">
              <img
                src={logoSrc}
                alt="Bonzah"
                className="w-5 h-5 object-contain"
                onError={(e: any) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="text-xs font-semibold text-gray-700">Bonzah available</span>
            </div>
          );
        })()}
        {/* {simpleImg ? ( */}
        <img
          src={
            cardPhotoUrl(data) ?? "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
          }
          alt={name}
          className="h-[200px] object-cover w-full"
        />
      </div>
    );
  };

  const renderContent = () => {
    return (
      // flex-col + gap (not space-y) so the price block can sit at the bottom with mt-auto and
      // every card in a grid row ends at the same height (1NOW-11110).
      <div className={`flex flex-col flex-1 ${size === "default" ? "p-4 gap-4" : "p-3 gap-2"}`}>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2
              className={`font-medium capitalize min-w-0 flex-1 ${
                size === "default" ? "text-lg" : "text-base"
              }`}
            >
              <span
                className="block truncate text-text-color whitespace-nowrap overflow-hidden overflow-ellipsis"
                style={{ textOverflow: "ellipsis" }}
                title={name}
              >
                {name}
              </span>
            </h2>
            {hasDiscount && (
              <span className="shrink-0 text-sm font-bold tracking-wide text-button-color">
                {discountPercent}% OFF
              </span>
            )}
          </div>

          {/* A spec with no value is not shown, and with none the row is gone (1NOW-11141).
              The price block's mt-auto keeps every card in a row the same height. */}
          {(seatsNum || colorText) && (
            <span className="text-text-color text-sm">
              {seatsNum && (
                <>
                  <span className="font-semibold">{seatsNum}</span>{" "}
                  {seatsNum === 1 ? "seat" : "seats"}
                </>
              )}
              {seatsNum && colorText && " · "}
              {colorText && (
                <>
                  <span className="font-semibold">{colorText}</span> color
                </>
              )}
            </span>
          )}
        </div>
        <div className="w-14 border-b border-secondary-100 "></div>
        {/* The price block is pinned to the bottom and its tier area has a fixed height, so the
            main price is at the same height in every card of a row (1NOW-11110). */}
        <div className="mt-auto flex flex-col">
          {noPrice && (
            <div className="flex flex-col items-start gap-2">
              <span className="text-lg font-bold text-text-color leading-tight">
                Price on request
              </span>
              {!areaShown && requestButton}
            </div>
          )}
          {!noPrice && currentPriceNum !== 0 && (
            <>
              {hasDiscount && primaryPeriod.kind === "day" && (
                <span className="text-sm font-medium text-secondary-text-color line-through leading-tight">
                  {"$" + formatPrice(increasedPriceNum)}
                </span>
              )}
              <span className="text-lg font-bold text-text-color leading-tight">
                {primaryPeriod.formatted}
                {size === "default" && (
                  <span className="text-sm text-secondary-text-color font-normal ml-0.5">
                    {primaryPeriod.label}
                  </span>
                )}
              </span>
            </>
          )}
          {areaShown && (
            <div
              className={`mt-1.5 pt-1.5 border-t ${
                !noPrice && secondaryPeriods.length > 0 ? "border-secondary-100" : "border-transparent"
              }`}
            >
              <div style={{ minHeight: priceAreaHeight(slots) }}>
                {requestButton && <div className="flex">{requestButton}</div>}
                {!noPrice && currentPriceNum !== 0 && secondaryPeriods.length > 0 && (
                  <>
                    <div
                      className="grid grid-cols-[auto_auto] justify-start items-center gap-x-2 leading-tight"
                      style={{ gridAutoRows: TIER_ROW_PX }}
                    >
                      {secondaryPeriods.map((p) => (
                        <div key={p.days} className="contents">
                          <span className="text-sm font-medium text-secondary-text-color">
                            {p.formatted}
                          </span>
                          <span className="text-xs font-normal text-secondary-text-color">
                            {p.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        // The whole card is clickable, so expanding in place
                        // has to stop the click reaching it.
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setTiersExpanded(true);
                        }}
                        className="mt-1.5 block text-xs leading-4 font-medium text-secondary-text-color underline underline-offset-2"
                        style={{ height: TIER_MORE_PX - 6 }}
                      >
                        {moreRatesLabel(hiddenCount)}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-StayCard group relative flex flex-col h-full rounded-md border border-secondary-100 overflow-hidden will-change-transform hover:shadow-xl transition-shadow ${className} ${
        onClick && !noPrice ? "cursor-pointer" : "cursor-default"
      }`}
      data-nc-id="StayCard"
      onClick={noPrice ? undefined : onClick}
    >
      {renderSliderGallery()}
      {renderContent()}
    </div>
  );
};

export default FleetCard;
